import type { OfflineQueue } from "common/components/types";
import { logError } from "common/debug-print";
import { exceptionToString, isArray } from "common/Support";
import { localStorageGetItem, localStorageSetItem } from "support/local-storage";
import { addOnlineHandler, deleteOnlineHandler, getIsOnline, NetworkStatus } from "hooks/use-network-status";

export class OfflineQueueImpl<T> implements OfflineQueue<T> {
    private readonly jobsProcessing = new Set<string>();

    constructor(
        private readonly name: string,
        private readonly onOnline: () => void,
        private readonly idForItem: (item: T) => string
    ) {
        addOnlineHandler(this.onlineChanged);
    }

    private readonly onlineChanged = (online: NetworkStatus) => {
        if (online !== NetworkStatus.Offline) {
            this.onOnline();
        }
    };

    private get localStorageKey(): string {
        return "offline-queue-" + this.name;
    }

    private loadQueue(): readonly T[] {
        try {
            const queueString = localStorageGetItem(this.localStorageKey);
            if (queueString === undefined) return [];

            const queue = JSON.parse(queueString);
            if (!isArray(queue)) {
                logError("offline queue is not an array", this.name, queueString);
                return [];
            }

            return queue as T[];
        } catch (e: unknown) {
            logError("Error loading offline queue", exceptionToString(e));
            return [];
        }
    }

    private saveQueue(queue: readonly T[]): void {
        localStorageSetItem(this.localStorageKey, JSON.stringify(queue));
    }

    public enqueue(item: T): void {
        this.saveQueue([...this.loadQueue(), item]);
    }

    // If `processItem` did process the item, it must return `true`, in which
    // case the queue removes the item.
    public async processIfOnline(processItem: (item: T) => Promise<boolean>): Promise<void> {
        if (!getIsOnline()) return;

        for (;;) {
            const queue = this.loadQueue();

            let allSkipped = true;
            for (const item of queue) {
                const id = this.idForItem(item);
                if (this.jobsProcessing.has(id)) continue;

                this.jobsProcessing.add(id);

                let didProcess: boolean;
                try {
                    didProcess = await processItem(item);
                } catch (e: unknown) {
                    logError("Error processing offline queue item", this.name, item, exceptionToString(e));
                    didProcess = true;
                }

                if (didProcess) {
                    allSkipped = false;
                    this.saveQueue(
                        this.loadQueue().filter(i => {
                            return this.idForItem(i) !== id;
                        })
                    );
                }

                this.jobsProcessing.delete(id);
            }

            if (allSkipped) break;
        }
    }

    public forEach(processItem: (item: T) => void): void {
        const queue = this.loadQueue();
        for (const item of queue) {
            processItem(item);
        }
    }

    public retire(): void {
        deleteOnlineHandler(this.onlineChanged);
    }
}
